import React, { useState, useEffect } from 'react';
import { createStyles, WithStyles, withStyles } from '@material-ui/core';
import { FormikHelpers } from 'formik';
import * as Yup from 'yup';

import { Form } from '../Form';
import { useTranslator, TFunction } from '../../i18n/useTranslator';
import { Button } from '../Button';
import { FormContent } from '../FormContent';
import { FieldTextField } from '../form-fields/FieldTextField';
import { FormError } from '../FormError';
import { ErrorService } from '../../services/Error.service';
import { SubmissionService } from '../../services/Submissions.service';
import { Submission } from '../../shared/types/Submission';
import { Autosave } from '../Autosave';
import { NoteStatusEnum } from '../../shared/enums/NoteStatusEnum';
import { Note } from '../../shared/types/Note';

const styles = () =>
  createStyles({
    control: {
      marginBottom: 10,
    },
    addButton: {
      alignSelf: 'flex-end',
    },
  });

type Props = WithStyles<typeof styles> & {
  submissionId: Submission['id'];
  initValues?: Note;
  onSaved: () => void;
};

export type NoteFormValues = {
  note: string;
};

const NoteFormComponent: React.FunctionComponent<Props> = ({ classes, submissionId, initValues, onSaved }) => {
  const [serverError, setServerError] = useState('');
  const [autosaving, setAutosaving] = useState(false);
  const [dataToSubmit, setDataToSubmit] = useState<NoteFormValues>();
  const [formHelpers, setFormHelpers] = useState<FormikHelpers<NoteFormValues>>();

  const { t } = useTranslator();

  const validationSchema = (t: TFunction) => Yup.object().shape({});

  const [initialValues, setInitialValues] = useState<NoteFormValues>({
    note: initValues ? initValues.value : '',
  });

  const handleSaveNote = () => {
    (async () => {
      if (!dataToSubmit || autosaving) {
        return;
      }

      try {
        const formData = new FormData();

        formData.append("value", dataToSubmit.note);
        formData.append("parentId", submissionId);
        formData.append('status', NoteStatusEnum.Submitted.toString());

        await SubmissionService.addNote(formData);

        formHelpers?.resetForm();
        onSaved();
      } catch (error) {
        setServerError(ErrorService.parseError(error));
      }
      finally {
        setDataToSubmit(undefined);
      }
    })();
  };

  useEffect(handleSaveNote, [dataToSubmit, autosaving]);

  const handleFormSubmit = (data: NoteFormValues, formHelpers: FormikHelpers<NoteFormValues>) => {
    setServerError("");
    setDataToSubmit(data);
    setFormHelpers(formHelpers);
  };

  const handleAutosave = async (values: NoteFormValues) => {
    setAutosaving(true);
    
    try {
      const formData = new FormData();

      formData.append("value", values.note);
      formData.append("parentId", submissionId);
      formData.append('status', NoteStatusEnum.Autosaved.toString());

      await SubmissionService.addNote(formData);

      setInitialValues(values);
    }
    finally {
      setAutosaving(false);
    }
  };

  return (
    <Form initialValues={initialValues} validationSchema={validationSchema(t)} onSubmit={handleFormSubmit}>
      {({ isSubmitting, values }) => (
        <FormContent>
          <FieldTextField
            className={classes.control}
            multiline
            name={'note'}
            placeholder={t('noteDetails')}
            rows={3}
            size={'small'}
            fullWidth
          />

          <FormError show={Boolean(serverError)} error={t(serverError)} />

          <Button
            className={classes.addButton}
            type={'submit'}
            loading={isSubmitting}
            disabled={!values.note || isSubmitting}
          >
            {t('addNote')}
          </Button>

          <Autosave submitting={isSubmitting} onSave={handleAutosave} />
        </FormContent>
      )}
    </Form>
  );
}

export const NoteForm = withStyles(styles)(NoteFormComponent);
